import { useCallback } from 'react';
import type { EventsAndLegs } from '../core/TimeData';
import { isTimeData, isLoadEventTimeData } from '../core/TimeData';
import type { EventTimeline } from '../core/Timelines';
import { useCarbonPrice } from '../hooks/useCarbonPrice';
import { euEtsCountriesSet } from './EU_ETS_countries';
import { groupTimeDatasByShipment } from './shipment';
import { countTotalMassDistance } from '../core/Core.voyage-metrics';
import type { Distances } from '../distances/context';
import { getEventsFinancialBounds } from './timelineBounds';

export const DEFAULT_CARBON_FACTOR = 3.206;

/**
 * EU ETS has a phase-in period during which only a part of total emissions
 * are considered
 *
 * Before 2024: 0%
 * 2024 - 40%
 * 2025 - 70%
 * 2026 onwards - 100%
 */
export function getEtsTransitionFactor(year: number): number {
  if (year < 2024) return 0;
  if (year === 2024) return 0.4;
  if (year === 2025) return 0.7;
  return 1.0;
}

export function getTimelineOrderCountries(timeline: EventTimeline) {
  const countries = new Set<string>();

  timeline.voyageGroups.forEach((vg) =>
    vg.orders.forEach((order) => {
      if (order.locationByLoadPortId?.country) {
        countries.add(order.locationByLoadPortId.country);
      }

      if (order.locationByDischargePortId?.country) {
        countries.add(order.locationByDischargePortId?.country);
      }
    }),
  );

  return Array.from(countries);
}

export function getShipmentEtsFactor(
  shipmentCountries: string[],
  vesselGrossTonnage: number | null,
) {
  if (!vesselGrossTonnage) return 0;
  const isGTOver5000 = vesselGrossTonnage >= 5000;
  if (!isGTOver5000) return 0;

  const euCountries = shipmentCountries.filter((c) => euEtsCountriesSet.has(c));
  if (euCountries.length === shipmentCountries.length) return 1;
  if (euCountries.length > 0) return 0.5;
  return 0;
}

export function getTimeDataOrderCountries(tds: EventsAndLegs) {
  const countries = new Set<string>();

  tds.forEach((td) => {
    if (!isTimeData(td) || !isLoadEventTimeData(td)) return;
    const { order } = td.event;

    if (order.locationByLoadPortId?.country) {
      countries.add(order.locationByLoadPortId.country);
    }

    if (order.locationByDischargePortId?.country) {
      countries.add(order.locationByDischargePortId?.country);
    }
  });

  return Array.from(countries);
}

/**
 * Returns sum of ETS and EEOI costs for all shipments included in the given
 * vessel timeline. Not applicable to contract (COA/Spot) timelines.
 *
 * ETS (EU Emissions Trading System):
 * For vessels with Gross Tonnage over 5000, an additional requirement applies
 * where they must pay for 100% of emissions for voyages between EU ports and
 * 50% of emissions for voyages between an EU and non-EU port.
 *
 * EEOI (Energy Efficiency Operating Indicator):
 * Measures amount of CO2 produced per ton-mile
 */
export function getTimelineEts(
  timeline: EventTimeline,
  carbonPrice: number,
  distances: Distances,
) {
  if (timeline.type !== 'tc') return null;

  let ets = 0;
  let eeoi = 0;

  const tdsByShipment = groupTimeDatasByShipment(
    timeline.voyageGroups.flatMap((vg) => vg.eventsAndLegs),
  );

  for (const [_shipmentId, tds] of tdsByShipment) {
    let shipmentCO2 = 0;

    const { startTime: shipmentStartTime } = getEventsFinancialBounds(tds);

    if (!shipmentStartTime) {
      continue;
    }

    const shipmentStartYear = new Date(shipmentStartTime).getUTCFullYear();
    const etsTransitionFactor = getEtsTransitionFactor(shipmentStartYear);

    const etsFactor = getShipmentEtsFactor(
      getTimeDataOrderCountries(tds),
      Number(timeline.vessel.grossTonnage || 0),
    );

    for (const td of tds) {
      if (!('totalCO2' in td)) continue;
      shipmentCO2 += td.totalCO2;
    }

    const shipmentTonMiles = countTotalMassDistance(
      tds.filter(isTimeData),
      distances,
    );

    const shipmentEts =
      shipmentCO2 * carbonPrice * etsFactor * etsTransitionFactor;
    const shipmentEeoi =
      shipmentTonMiles > 0 ? (1_000_000 * shipmentCO2) / shipmentTonMiles : 0;

    ets += shipmentEts;
    eeoi += shipmentEeoi;
  }

  return { eeoi, ets };
}

export function useGetTimelineEts(distances: Distances) {
  const { carbonPrice } = useCarbonPrice();

  const getTimelineEtsCallback = useCallback(
    (timeline: EventTimeline) => {
      if (!carbonPrice) return null;

      return getTimelineEts(timeline, carbonPrice, distances);
    },
    [carbonPrice, distances],
  );

  return {
    getTimelineEts: getTimelineEtsCallback,
  };
}
